<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.company_group_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="showSlideOverAddGroup = true">
                {{ $t('labels.addCompanyGroup') }}
            </hts-form-button>
        </div>
        <hts-data-table
            ref="dataTable"
            :headers="companyGroupHeaders()"
            :server="server"
            :use-archive="useCompanyGroupArchive"
            use-selection
            store-name="companyGroupDataTable"
            store-key="CompanyGroup" />
        <add-group
            :show="showSlideOverAddGroup"
            :size-slide-over="30"
            @refresh-tables="refreshTables()"
            @close="showSlideOverAddGroup = false" />
        <edit-group
            :group-is-selected-to-edit="groupIsSelectedToEdit"
            :show="showSlideOverEditGroup"
            :size-slide-over="30"
            @refresh-tables="refreshTables()"
            @close="showSlideOverEditGroup = false" />
        <update-permissions
            :group-edit="groupIsSelectedToEdit"
            :show="showSlideOverUpdatePermissions"
            :size-slide-over="50"
            @refresh-tables="refreshTables()"
            @close="handleClose" />
        <hts-modal
            :show="showPopupDeleteGroup"
            :title="$t('labels.deleteGroup')"
            type="confirm"
            :content="$t('labels.confirmDeleteGroup')"
            @hide="closePopupDeleteGroup"
            @confirm="handleDeleteGroup" />
    </div>
</template>

<script setup lang="ts">
import AddGroup from './-components/add-group.vue'
import EditGroup from './-components/edit-group.vue'
import dataForm, { ICompanyGroup } from './-components/imports/model'
import UpdatePermissions from './-components/update-permissions.vue'
import layoutPermissions from '~/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
const { $formatters } = useNuxtApp()

const server = ref({})
onBeforeMount(() => {
  server.value = $url.companyGroup.getList
})
const dataTable = ref()

const refreshTables = () => {
  Object.assign(groupIsSelectedToEdit, { ...dataForm.initValue })
  if (dataTable.value) {
    dataTable.value.refresh()
  }
}

const useCompanyGroupArchive = computed(() => {
  return {
    api: $url.company.companyGroupArchive,
    permissions: checkPermission(layoutPermissions.company_group_archive)
  }
})

const companyGroupId = ref('')
const showSlideOverAddGroup = ref(false)
const showSlideOverEditGroup = ref(false)
const showSlideOverUpdatePermissions = ref(false)
const showPopupDeleteGroup = ref(false)
const groupIsSelectedToDelete = reactive<ICompanyGroup>({
  name: '',
  description: '',
  permissions: []
})
const groupIsSelectedToEdit = reactive<ICompanyGroup>({
  name: '',
  description: '',
  permissions: []
})
const handleUpdatePermissions = (group: any) => {
  Object.assign(groupIsSelectedToEdit, group)
  showSlideOverUpdatePermissions.value = true
}
const handleEditGroup = (group: any) => {
  Object.assign(groupIsSelectedToEdit, group)
  showSlideOverEditGroup.value = true
}
const openDelete = (group: any) => {
  if (!group) {
    return null
  }
  showPopupDeleteGroup.value = true
  companyGroupId.value = group._id
}

const handleDeleteGroup = () => {
  const api = $url.companyGroup.deleteGroup
  $api(api, { body: { id: companyGroupId.value } })
    .then((data) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (!responseData) {
        return true
      }
      if (responseData.success) {
        refreshTables()
        process.client && $toast().success($t('labels.deleteGroupSuccess'))
        closePopupDeleteGroup()
      }
    })
    .catch((error: any) => error)
}
const closePopupDeleteGroup = () => {
  showPopupDeleteGroup.value = false
  Object.assign(groupIsSelectedToDelete, {})
}
const handleClose = () => {
  showSlideOverUpdatePermissions.value = false
  setTimeout(() => {
    Object.assign(groupIsSelectedToEdit, { ...dataForm.initValue })
  }, 500)
}

const companyGroupHeaders = () => {
  return [
    {
      field: 'name',
      headerName: $t('labels.groupName'),
      sortable: true,
      filter: 'agTextColumnFilter'
    },
    {
      field: 'description',
      headerName: $t('labels.description'),
      sortable: true,
      filter: 'agTextColumnFilter'
    },
    {
      field: 'createdAt',
      headerName: $t('labels.createdAt'),
      sortable: true,
      valueGetter: (params: any) => {
        return $formatters.datetime(params.data.createdAt)
      }
    },
    {
      field: 'updatedAt',
      headerName: $t('labels.updatedAt'),
      sortable: true,
      valueGetter: (params: any) => {
        return $formatters.datetime(params.data.updatedAt)
      }
    },
    {
      field: 'action',
      pinned: 'right',
      sortable: false,
      lockPosition: true,
      lockPinned: true,
      headerName: $t('labels.action'),
      headerClass: 'ag-header-text-center',
      cellStyle: { overflow: 'visible' },
      cellRenderer: 'DataTableActions',
      cellRendererParams: {
        storeKey: 'companyGroupDataTable',
        storeName: 'CompanyGroup',
        items: [
          {
            icon: 'hi-pencil-icon',
            label: $t('labels.edit'),
            class: 'text-blue-600 hover:text-blue-500',
            permissions: checkPermission(layoutPermissions.company_group_edit),
            click: handleEditGroup
          },
          {
            icon: 'hi-pencil-icon',
            label: $t('labels.updatePermissions'),
            class: 'text-blue-600 hover:text-blue-500',
            permissions: checkPermission(layoutPermissions.company_group_update_permissions),
            validate: (group: any) => !group.isAdmin,
            click: handleUpdatePermissions
          },
          {
            icon: 'hi-trash-icon',
            label: $t('labels.delete'),
            class: 'text-error-600 hover:text-error-500',
            permissions: checkPermission(layoutPermissions.company_group_delete),
            isArchive: true,
            click: openDelete
          }
        ]
      }
    }
  ]
}
</script>
